import { getUserDisplayName } from "../lib/userDisplayName";
import { SocialLinks } from "../components/community/SocialLinks";
import { CommunityAvatar } from "../components/community/CommunityAvatar";
import { CommunityPostShare } from "../components/community/CommunityPostShare";
import { CommunityBody } from "../components/community/content/CommunityBody";
import { purposeFromTags } from "../lib/communityParticipation";
import "../styles/community-renewal.css";
import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Bookmark,
  ChevronDown,
  Flag,
  Heart,
  Image as ImageIcon,
  MessageSquare,
  Paperclip,
  Pencil,
  Pin,
  Send,
  ShieldOff,
  Trash2,
  UserMinus,
  UserPlus,
} from "lucide-react";
import {
  apiClient,
  CommunityComment,
  CommunityPostDetail,
  CommunityUserProfile,
  getMediaUrl,
} from "../lib/api";
import { downloadUrlAs } from "../lib/downloadStorageFile";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/Button";
import { usePageSEO } from "../lib/usePageSEO";
import { extractYouTubeVideoId } from "../lib/extractYouTubeVideoId";

function useImagePreview(file: File | null) {
  const [url, setUrl] = React.useState("");
  React.useEffect(() => {
    if (!file) {
      setUrl("");
      return;
    }
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);
  return url;
}

async function downloadCommunityMediaFile(
  mediaUrl: string | null | undefined,
  displayName: string,
): Promise<void> {
  if (!mediaUrl) throw new Error("파일 링크가 없습니다.");
  await downloadUrlAs(getMediaUrl(mediaUrl), displayName);
}

/**
 * 이미지 압축 및 리사이즈 함수
 * 최대 가로 1920px, JPEG 품질 85%로 최적화하여 파일 크기 감소
 */
async function compressImage(
  file: File,
  maxWidth: number = 1920,
  quality: number = 0.85,
): Promise<File> {
  return new Promise((resolve, reject) => {
    // 이미지가 아닌 경우 원본 반환
    if (!file.type.startsWith("image/")) {
      resolve(file);
      return;
    }

    // 파일 크기가 500KB 미만이면 압축하지 않음 (이미 작은 파일)
    if (file.size < 500 * 1024) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // 원본 크기 확인
        const originalWidth = img.width;
        const originalHeight = img.height;

        // 최대 크기보다 작으면 압축만 수행
        if (originalWidth <= maxWidth) {
          // 품질만 조정하여 압축
          const canvas = document.createElement("canvas");
          canvas.width = originalWidth;
          canvas.height = originalHeight;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            resolve(file);
            return;
          }
          ctx.drawImage(img, 0, 0);

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                resolve(file);
                return;
              }
              const compressedFile = new File([blob], file.name, {
                type: "image/jpeg",
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            },
            "image/jpeg",
            quality,
          );
          return;
        }

        // 리사이즈 필요
        const aspectRatio = originalHeight / originalWidth;
        const newWidth = maxWidth;
        const newHeight = Math.round(maxWidth * aspectRatio);

        const canvas = document.createElement("canvas");
        canvas.width = newWidth;
        canvas.height = newHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(file);
          return;
        }

        // 고품질 리사이즈
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        // JPEG로 변환 및 압축
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(file);
              return;
            }
            const compressedFile = new File(
              [blob],
              file.name.replace(/\.[^/.]+$/, ".jpg"),
              {
                type: "image/jpeg",
                lastModified: Date.now(),
              },
            );
            resolve(compressedFile);
          },
          "image/jpeg",
          quality,
        );
      };
      img.onerror = () => resolve(file); // 에러 시 원본 반환
      img.src = e.target?.result as string;
    };
    reader.onerror = () => resolve(file); // 에러 시 원본 반환
    reader.readAsDataURL(file);
  });
}

function sanitizeHtml(html: string) {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    // 기존 iframe 제거 (안전을 위해)
    doc.querySelectorAll("script,iframe").forEach((n) => n.remove());
    doc.querySelectorAll("*").forEach((el) => {
      Array.from(el.attributes).forEach((attr) => {
        if (attr.name.toLowerCase().startsWith("on"))
          el.removeAttribute(attr.name);
      });
    });
    // 이미지와 링크의 상대 경로를 절대 경로로 변환
    // CLS 개선: 모든 이미지에 반응형 크기 및 lazy loading 추가 (원본 비율 유지)
    doc.querySelectorAll("img").forEach((img) => {
      const src = img.getAttribute("src");
      if (src) {
        img.setAttribute("src", getMediaUrl(src));
      }

      // CLS 방지: 가로 폭만 화면에 맞추고 원본 비율 유지
      if (!img.hasAttribute("width") && !img.hasAttribute("height")) {
        img.style.width = "100%";
        img.style.height = "auto";
        img.style.maxWidth = "100%";
        // aspect-ratio는 제거 - 원본 비율 유지
      }

      // Lazy loading으로 초기 렌더링 성능 개선
      img.setAttribute("loading", "lazy");
      img.setAttribute("decoding", "async");
    });

    // YouTube 링크를 iframe으로 변환
    doc.querySelectorAll("a").forEach((a) => {
      const href = a.getAttribute("href");
      if (!href) return;

      // 상대 경로 처리
      if (href.startsWith("/media/") || href.startsWith("/static/")) {
        a.setAttribute("href", getMediaUrl(href));
        return;
      }

      // YouTube URL인 경우 iframe으로 변환
      const youtubeId = extractYouTubeVideoId(href);
      if (youtubeId) {
        // CLS 개선: wrapper에 명시적 min-height 추가
        const wrapper = doc.createElement("div");
        wrapper.className = "youtube-embed-wrapper";
        wrapper.style.margin = "1rem 0";
        wrapper.style.position = "relative";
        wrapper.style.paddingBottom = "56.25%"; // 16:9 aspect ratio
        wrapper.style.height = "0";
        wrapper.style.minHeight = "315px"; // CLS 방지를 위한 최소 높이
        wrapper.style.overflow = "hidden";
        wrapper.style.backgroundColor = "#f3f4f6"; // 로딩 중 배경색

        const iframe = doc.createElement("iframe");
        iframe.src = `https://www.youtube.com/embed/${youtubeId}`;
        iframe.width = "560";
        iframe.height = "315";
        iframe.frameBorder = "0";
        iframe.allow =
          "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture";
        iframe.allowFullscreen = true;
        iframe.loading = "lazy"; // Lazy loading 추가
        iframe.style.position = "absolute";
        iframe.style.top = "0";
        iframe.style.left = "0";
        iframe.style.width = "100%";
        iframe.style.height = "100%";
        iframe.style.border = "0";

        wrapper.appendChild(iframe);
        a.parentNode?.replaceChild(wrapper, a);
      }
    });

    return doc.body.innerHTML;
  } catch {
    return html;
  }
}

function resolveEmbeddableHttpsUrl(
  raw: string | null | undefined,
): string | null {
  const value = raw?.trim();
  if (!value) return null;
  try {
    const parsed = new URL(value);
    return parsed.protocol === "https:" ? parsed.toString() : null;
  } catch {
    return null;
  }
}

/** 댓글 평문에서 http(s) 및 www. 주소를 새 창 링크로 표시 */
function linkifyCommentContent(text: string): React.ReactNode {
  const re = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;
  const stripTrailing = (raw: string): { core: string; tail: string } => {
    let core = raw;
    let tail = "";
    while (core.length > 0 && /[.,;:!?)}\]'"]$/u.test(core)) {
      tail = core.slice(-1) + tail;
      core = core.slice(0, -1);
    }
    return { core, tail };
  };
  const parts: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  re.lastIndex = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const raw = m[1];
    const { core, tail } = stripTrailing(raw);
    if (core) {
      const href = /^www\./i.test(core) ? `https://${core}` : core;
      parts.push(
        <a
          key={`u-${key++}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#526a2e] hover:underline break-all"
        >
          {core}
        </a>,
      );
    }
    if (tail) parts.push(tail);
    last = m.index + raw.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? <>{parts}</> : text;
}

/** 루트 제외, 트리의 모든 대댓글을 표시 순서(깊이 우선)대로 한 배열로 펼침 */
function flattenCommentReplies(
  nodes: CommunityComment[] | undefined,
): CommunityComment[] {
  const out: CommunityComment[] = [];
  if (!nodes?.length) return out;
  for (const n of nodes) {
    out.push(n);
    out.push(...flattenCommentReplies(n.replies));
  }
  return out;
}

function patchCommentLikeInTree(
  comments: CommunityComment[],
  commentId: number,
  liked: boolean,
  numLikes: number,
): CommunityComment[] {
  return comments.map((c) => {
    if (c.id === commentId)
      return { ...c, is_liked: liked, num_likes: numLikes };
    if (c.replies?.length) {
      return {
        ...c,
        replies: patchCommentLikeInTree(c.replies, commentId, liked, numLikes),
      };
    }
    return c;
  });
}

function CommentItem({
  c,
  postId,
  isFlattenedReply,
  onChanged,
  onLikeToggled,
  onAuthRequired,
}: {
  c: CommunityComment;
  postId: number;
  /** true면 카드만 렌더하고 자식은 루트가 한꺼번에 펼쳐서 형제로 둠 (계단 들여쓰기 방지) */
  isFlattenedReply?: boolean;
  onChanged: () => Promise<void>;
  onLikeToggled: (commentId: number, liked: boolean, numLikes: number) => void;
  onAuthRequired: (message: string) => void;
}) {
  const nav = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [replyOpen, setReplyOpen] = React.useState(false);
  const [replyText, setReplyText] = React.useState("");
  const [replyImage, setReplyImage] = React.useState<File | null>(null);
  const [replyFile, setReplyFile] = React.useState<File | null>(null);
  const replyImageUrl = useImagePreview(replyImage);
  const [actionPending, setActionPending] = React.useState(false);
  const actionLock = React.useRef(false);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [editOpen, setEditOpen] = React.useState(false);
  const [editText, setEditText] = React.useState(c.is_deleted ? "" : c.content);
  const [editImage, setEditImage] = React.useState<File | null>(null);
  const [editFile, setEditFile] = React.useState<File | null>(null);
  const editImageUrl = useImagePreview(editImage);
  const replyImageInputRef = React.useRef<HTMLInputElement>(null);
  const replyFileInputRef = React.useRef<HTMLInputElement>(null);
  const editImageInputRef = React.useRef<HTMLInputElement>(null);
  const editFileInputRef = React.useRef<HTMLInputElement>(null);
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const [commentLikeLoading, setCommentLikeLoading] = React.useState(false);
  const [authorProfile, setAuthorProfile] =
    React.useState<CommunityUserProfile | null>(null);
  const [commentAuthorMemo, setCommentAuthorMemo] = React.useState<{
    content: string;
    color: string | null;
  } | null>(null);
  const [followLoading, setFollowLoading] = React.useState(false);
  const [memoFormOpen, setMemoFormOpen] = React.useState(false);
  const [memoInput, setMemoInput] = React.useState("");
  const [memoColor, setMemoColor] = React.useState("#FF0000");
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const isMine =
    c.can_edit ||
    (!!user && !!c.author?.id && c.author.id === user.id) ||
    !!user?.is_superuser;
  const isCommentAuthorMine =
    !!user && !!c.author?.id && c.author.id === user.id;

  React.useEffect(() => {
    if (!c.author?.id || isCommentAuthorMine || !user) return;
    apiClient
      .getCommunityUserMemo(c.author.id)
      .then((r) =>
        r.content
          ? setCommentAuthorMemo({ content: r.content, color: r.color })
          : setCommentAuthorMemo(null),
      )
      .catch(() => setCommentAuthorMemo(null));
  }, [c.author?.id, isCommentAuthorMine, user]);

  React.useEffect(() => {
    if (!dropdownOpen || !c.author?.id || isCommentAuthorMine) return;
    apiClient
      .getCommunityUserProfile(c.author.id)
      .then((p) => {
        setAuthorProfile(p);
        if (p.memo)
          setCommentAuthorMemo({
            content: p.memo.content,
            color: p.memo.color,
          });
        else setCommentAuthorMemo(null);
      })
      .catch(() => setAuthorProfile(null));
  }, [dropdownOpen, c.author?.id, isCommentAuthorMine]);

  React.useEffect(() => {
    if (!dropdownOpen) return;
    const close = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      )
        setDropdownOpen(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [dropdownOpen]);

  const handleCommentAuthorFollow = async () => {
    if (!c.author?.id || isCommentAuthorMine || followLoading) return;
    setFollowLoading(true);
    try {
      const res = await apiClient.toggleCommunityFollow(c.author.id);
      setAuthorProfile((prev) =>
        prev
          ? {
              ...prev,
              is_following: res.followed,
              followers_count: prev.followers_count + (res.followed ? 1 : -1),
            }
          : prev,
      );
    } catch (error: unknown) {
      setActionError(
        error instanceof Error
          ? error.message
          : "팔로우 상태를 변경하지 못했어요.",
      );
    } finally {
      setFollowLoading(false);
      setDropdownOpen(false);
    }
  };
  const openCommentMemoForm = () => {
    setMemoInput(authorProfile?.memo?.content ?? "");
    setMemoColor(authorProfile?.memo?.color ?? "#FF0000");
    setMemoFormOpen(true);
    setDropdownOpen(false);
  };
  const saveCommentMemo = async () => {
    if (!c.author?.id || isCommentAuthorMine) return;
    try {
      const res = await apiClient.setCommunityUserMemo(c.author.id, {
        content: memoInput.slice(0, 10),
        color: memoColor,
      });
      setAuthorProfile((prev) =>
        prev
          ? { ...prev, memo: { content: res.content, color: res.color } }
          : prev,
      );
      setCommentAuthorMemo(
        res.content ? { content: res.content, color: res.color } : null,
      );
      setMemoFormOpen(false);
      setMemoInput("");
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : "메모를 저장하지 못했습니다.");
    }
  };

  const runCommentAction = async (action: () => Promise<void>) => {
    if (actionLock.current) return;
    actionLock.current = true;
    setActionPending(true);
    setActionError(null);
    try {
      await action();
    } catch (error: unknown) {
      setActionError(
        error instanceof Error
          ? error.message
          : "요청을 처리하지 못했어요. 다시 시도해 주세요.",
      );
    } finally {
      actionLock.current = false;
      setActionPending(false);
    }
  };

  const report = async () => {
    if (!isAuthenticated) return;
    const reason = prompt("신고 사유를 입력해주세요(선택)");
    if (reason === null) return;
    await apiClient.reportCommunityComment(c.id, reason || "");
    alert("신고가 접수되었습니다. 검토 후 조치됩니다.");
  };

  const blockUser = async () => {
    if (!isAuthenticated) return;
    if (!c.author?.id) return;
    if (confirm("이 사용자를 차단할까요? (나에게만 안 보이게 됩니다)")) {
      await apiClient.toggleCommunityBlock(c.author.id);
      alert("차단 처리되었습니다.");
      await onChanged();
    }
  };

  const submitReply = async () => {
    if (!isAuthenticated) return;
    const text = replyText.trim();
    if (!text) return;
    await apiClient.createCommunityComment(postId, {
      content: text,
      parent_id: c.id,
      comment_image: replyImage,
      comment_file: replyFile,
    });
    setReplyText("");
    setReplyImage(null);
    setReplyFile(null);
    setReplyOpen(false);
    await onChanged();
  };

  const submitEdit = async () => {
    const text = editText.trim();
    if (!text) return;
    await apiClient.updateCommunityComment(c.id, {
      content: text,
      comment_image: editImage,
      comment_file: editFile,
    });
    setEditOpen(false);
    await onChanged();
  };

  const del = async () => {
    if (!confirm("댓글을 삭제할까요?")) return;
    await apiClient.deleteCommunityComment(c.id);
    await onChanged();
  };

  const toggleCommentLike = async () => {
    if (!isAuthenticated) {
      onAuthRequired("댓글 좋아요는 로그인 후 이용할 수 있어요.");
      return;
    }
    if (commentLikeLoading) return;
    setCommentLikeLoading(true);
    try {
      const res = await apiClient.toggleCommunityCommentLike(c.id);
      onLikeToggled(c.id, res.liked, res.num_likes);
    } catch (e: unknown) {
      alert(
        e instanceof Error ? e.message : "댓글 좋아요 처리에 실패했습니다.",
      );
    } finally {
      setCommentLikeLoading(false);
    }
  };

  const flatReplies = !isFlattenedReply ? flattenCommentReplies(c.replies) : [];

  const commentCardInner = (
    <>
      {actionError && (
        <p role="alert" className="fe-community-inline-error mb-3">
          {actionError}
        </p>
      )}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex items-center gap-2">
          <CommunityAvatar
            src={getMediaUrl(c.author.avatar_url)}
            name={getUserDisplayName(c.author)}
            className="w-8 h-8 border border-gray-100 text-xs font-semibold"
          />
          <div className="relative min-w-0" ref={dropdownRef}>
            <div className="inline-flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isCommentAuthorMine) setDropdownOpen((v) => !v);
                }}
                className="inline-flex items-center gap-0.5 text-sm font-semibold text-gray-900 truncate hover:text-[#526a2e] transition cursor-pointer"
              >
                {getUserDisplayName(c.author)}
                {!isCommentAuthorMine && (
                  <ChevronDown
                    className={`w-3.5 h-3.5 shrink-0 transition ${dropdownOpen ? "rotate-180" : ""}`}
                  />
                )}
              </button>
              {!c.is_deleted && <SocialLinks links={c.author.social_links} authorName={getUserDisplayName(c.author)} />}
              {commentAuthorMemo?.content && (
                <span
                  className="text-[10px] font-medium px-1.5 py-0.5 rounded"
                  style={{
                    backgroundColor: `${commentAuthorMemo.color ?? "#999"}20`,
                    color: commentAuthorMemo.color ?? "#333",
                  }}
                >
                  {commentAuthorMemo.content}
                </span>
              )}
            </div>
            {!isCommentAuthorMine && dropdownOpen && (
              <div className="absolute left-0 top-full mt-0.5 py-1 w-44 rounded-xl border border-gray-200 bg-white shadow-lg z-[100]">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    nav(
                      `/messages/compose?to=${encodeURIComponent(c.author?.username ?? "")}`,
                    );
                    setDropdownOpen(false);
                  }}
                  className="w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <Send className="w-3.5 h-3.5" /> 쪽지보내기
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openCommentMemoForm();
                  }}
                  className="w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <Pencil className="w-3.5 h-3.5" />{" "}
                  {authorProfile?.memo?.content ? "메모 수정" : "메모작성"}
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCommentAuthorFollow();
                  }}
                  disabled={followLoading}
                  className="w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
                >
                  {authorProfile?.is_following ? (
                    <UserMinus className="w-3.5 h-3.5" />
                  ) : (
                    <UserPlus className="w-3.5 h-3.5" />
                  )}
                  {authorProfile?.is_following ? "팔로우 끊기" : "팔로우하기"}
                </button>
              </div>
            )}
            <div className="text-xs text-gray-500 mt-0.5">
              {c.created_string ||
                new Date(c.created_at).toLocaleString("ko-KR")}
            </div>
          </div>
        </div>
      </div>

      {memoFormOpen && c.author?.id && !isCommentAuthorMine && (
        <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-2">
          <input
            type="text"
            maxLength={10}
            value={memoInput}
            onChange={(e) => setMemoInput(e.target.value)}
            placeholder="메모 (10자)"
            className="fe-input rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs w-28"
          />
          <div className="flex gap-0.5">
            {["#FF0000", "#0000FF", "#FFFF00", "#008000", "#FFA500"].map(
              (color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setMemoColor(color)}
                  className="w-5 h-5 rounded-full border-2 border-gray-200 hover:border-gray-400"
                  style={{
                    backgroundColor: color,
                    borderColor: memoColor === color ? "#374151" : undefined,
                  }}
                />
              ),
            )}
          </div>
          <button
            type="button"
            onClick={saveCommentMemo}
            className="fe-primary-action px-2.5 py-1.5 rounded-lg bg-[#435536] text-white text-xs font-semibold hover:bg-[#526a2e]"
          >
            저장
          </button>
          <button
            type="button"
            onClick={() => setMemoFormOpen(false)}
            className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs hover:bg-gray-50"
          >
            취소
          </button>
        </div>
      )}

      <div className="mt-3 text-sm text-gray-800 whitespace-pre-wrap">
        {c.is_deleted ? (
          <span className="text-gray-500">삭제된 댓글입니다</span>
        ) : (
          <span>{linkifyCommentContent(c.content)}</span>
        )}
      </div>

      {!c.is_deleted && (c.comment_image_url || c.comment_file_url) && (
        <div className="mt-3 space-y-2">
          {c.comment_image_url && (
            <a
              href={getMediaUrl(c.comment_image_url)}
              target="_blank"
              rel="noreferrer"
              title="원본 보기"
              className="inline-block max-w-full rounded-xl overflow-hidden border border-gray-200 bg-gray-50"
            >
              <img
                src={getMediaUrl(c.comment_image_url)}
                alt="댓글 첨부 이미지"
                className="max-h-40 sm:max-h-48 w-auto max-w-[min(100%,16rem)] object-contain align-bottom"
                loading="lazy"
                decoding="async"
              />
            </a>
          )}
          {c.comment_file_url && (
            <button
              type="button"
              onClick={() => {
                void downloadCommunityMediaFile(
                  c.comment_file_url,
                  c.comment_file_name || "첨부파일",
                ).catch((err: unknown) => {
                  alert(
                    err instanceof Error
                      ? err.message
                      : "다운로드에 실패했습니다.",
                  );
                });
              }}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm text-[#526a2e] hover:bg-gray-50"
            >
              <Paperclip className="w-4 h-4" />
              {c.comment_file_name || "첨부파일"}
            </button>
          )}
        </div>
      )}

      {!c.is_deleted && (
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={commentLikeLoading}
            onClick={toggleCommentLike}
            aria-pressed={c.is_liked}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border ${
              c.is_liked
                ? "bg-red-50 border-red-200 text-red-700"
                : "border-gray-200 bg-white hover:bg-gray-50"
            } disabled:opacity-60`}
          >
            <Heart
              className={`w-3.5 h-3.5 ${c.is_liked ? "fill-red-600 text-red-600" : ""}`}
            />
            좋아요 {c.num_likes ?? 0}
          </button>
          {isAuthenticated && (
            <button
              onClick={() => setReplyOpen((v) => !v)}
              className="px-3 py-1.5 rounded-xl text-xs font-semibold border border-gray-200 bg-white hover:bg-gray-50"
            >
              답글
            </button>
          )}
          {isMine && (
            <>
              <button
                onClick={() => setEditOpen((v) => !v)}
                className="px-3 py-1.5 rounded-xl text-xs font-semibold border border-gray-200 bg-white hover:bg-gray-50"
              >
                수정
              </button>
              <button
                disabled={actionPending}
                onClick={() => void runCommentAction(del)}
                className="px-3 py-1.5 rounded-xl text-xs font-semibold border border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
              >
                삭제
              </button>
            </>
          )}
          {isAuthenticated && !isMine && (
            <>
              <button
                disabled={actionPending}
                onClick={() => void runCommentAction(report)}
                className="px-3 py-1.5 rounded-xl text-xs font-semibold border border-gray-200 bg-white hover:bg-gray-50"
              >
                신고
              </button>
              <button
                disabled={actionPending}
                onClick={() => void runCommentAction(blockUser)}
                className="px-3 py-1.5 rounded-xl text-xs font-semibold border border-gray-200 bg-white hover:bg-gray-50"
              >
                차단
              </button>
            </>
          )}
        </div>
      )}

      {editOpen && (
        <div className="mt-3">
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            className="fe-input w-full rounded-2xl border border-gray-300 px-3 py-2 text-sm min-h-[90px] focus:outline-none focus:ring-2 focus:ring-[#9eb77a]"
          />
          <div className="mt-2 flex flex-wrap gap-3 items-center">
            <label
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-semibold ${
                editImage
                  ? "fe-community-selected cursor-pointer"
                  : "border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100 cursor-pointer"
              }`}
            >
              <ImageIcon className="w-4 h-4" />
              <span>이미지{editImage ? ` (${editImage.name})` : ""}</span>
              <input
                ref={editImageInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const compressed = await compressImage(file, 1920, 0.85);
                    setEditImage(compressed);
                  } else {
                    setEditImage(null);
                  }
                  // 같은 파일을 다시 선택할 수 있도록 value 초기화
                  if (e.target) {
                    e.target.value = "";
                  }
                }}
              />
            </label>
            <label
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-semibold ${
                editFile
                  ? "fe-community-selected cursor-pointer"
                  : "border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100 cursor-pointer"
              }`}
            >
              <Paperclip className="w-4 h-4" />
              <span>파일{editFile ? ` (${editFile.name})` : ""}</span>
              <input
                ref={editFileInputRef}
                type="file"
                className="hidden"
                onChange={(e) => {
                  setEditFile(e.target.files?.[0] || null);
                  // 같은 파일을 다시 선택할 수 있도록 value 초기화
                  if (e.target) {
                    e.target.value = "";
                  }
                }}
              />
            </label>
          </div>
          {editImage && (
            <div className="mt-2 relative">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-2 flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <img
                    src={editImageUrl}
                    alt="미리보기"
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-gray-700 truncate">
                      {editImage.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {(editImage.size / 1024).toFixed(1)} KB
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setEditImage(null)}
                  className="ml-2 px-2 py-1 rounded-lg text-xs font-semibold border border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                >
                  삭제
                </button>
              </div>
            </div>
          )}
          {editFile && !editImage && (
            <div className="mt-2">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-2 flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <Paperclip className="w-5 h-5 text-gray-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-gray-700 truncate">
                      {editFile.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {(editFile.size / 1024).toFixed(1)} KB
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setEditFile(null)}
                  className="ml-2 px-2 py-1 rounded-lg text-xs font-semibold border border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                >
                  삭제
                </button>
              </div>
            </div>
          )}
          <div className="mt-2 flex justify-end gap-2">
            <button
              onClick={() => setEditOpen(false)}
              className="px-3 py-2 rounded-xl text-sm font-semibold border border-gray-200 bg-white hover:bg-gray-50"
            >
              취소
            </button>
            <button
              disabled={actionPending}
              onClick={() => void runCommentAction(submitEdit)}
              className="fe-primary-action px-3 py-2 rounded-xl text-sm font-semibold bg-[#435536] text-white hover:bg-[#526a2e]"
            >
              저장
            </button>
          </div>
        </div>
      )}

      {replyOpen && (
        <div className="mt-3">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            className="fe-input w-full rounded-2xl border border-gray-300 px-3 py-2 text-sm min-h-[90px] focus:outline-none focus:ring-2 focus:ring-[#9eb77a]"
            placeholder="답글을 입력하세요…"
          />
          <div className="mt-2 flex flex-wrap gap-3 items-center">
            <label
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-semibold ${
                replyImage
                  ? "fe-community-selected cursor-pointer"
                  : "border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100 cursor-pointer"
              }`}
            >
              <ImageIcon className="w-4 h-4" />
              <span>이미지{replyImage ? ` (${replyImage.name})` : ""}</span>
              <input
                ref={replyImageInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const compressed = await compressImage(file, 1920, 0.85);
                    setReplyImage(compressed);
                  } else {
                    setReplyImage(null);
                  }
                  // 같은 파일을 다시 선택할 수 있도록 value 초기화
                  if (e.target) {
                    e.target.value = "";
                  }
                }}
              />
            </label>
            <label
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-semibold ${
                replyFile
                  ? "fe-community-selected cursor-pointer"
                  : "border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100 cursor-pointer"
              }`}
            >
              <Paperclip className="w-4 h-4" />
              <span>파일{replyFile ? ` (${replyFile.name})` : ""}</span>
              <input
                ref={replyFileInputRef}
                type="file"
                className="hidden"
                onChange={(e) => {
                  setReplyFile(e.target.files?.[0] || null);
                  // 같은 파일을 다시 선택할 수 있도록 value 초기화
                  if (e.target) {
                    e.target.value = "";
                  }
                }}
              />
            </label>
          </div>
          {replyImage && (
            <div className="mt-2 relative">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-2 flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <img
                    src={replyImageUrl}
                    alt="미리보기"
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-gray-700 truncate">
                      {replyImage.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {(replyImage.size / 1024).toFixed(1)} KB
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setReplyImage(null)}
                  className="ml-2 px-2 py-1 rounded-lg text-xs font-semibold border border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                >
                  삭제
                </button>
              </div>
            </div>
          )}
          {replyFile && !replyImage && (
            <div className="mt-2">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-2 flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <Paperclip className="w-5 h-5 text-gray-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-gray-700 truncate">
                      {replyFile.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {(replyFile.size / 1024).toFixed(1)} KB
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setReplyFile(null)}
                  className="ml-2 px-2 py-1 rounded-lg text-xs font-semibold border border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                >
                  삭제
                </button>
              </div>
            </div>
          )}
          <div className="mt-2 flex justify-end">
            <button
              disabled={actionPending}
              onClick={() => void runCommentAction(submitReply)}
              className="fe-primary-action px-3 py-2 rounded-xl text-sm font-semibold bg-[#435536] text-white hover:bg-[#526a2e]"
            >
              답글 작성
            </button>
          </div>
        </div>
      )}
    </>
  );

  if (isFlattenedReply) {
    return (
      <div className="fe-surface rounded-2xl border border-gray-200 bg-white p-4 ml-3 pl-3 border-l-2 border-gray-100 rounded-l-xl">
        {commentCardInner}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="fe-surface rounded-2xl border border-gray-200 bg-white p-4">
        {commentCardInner}
      </div>
      {flatReplies.map((r) => (
        <CommentItem
          key={r.id}
          c={r}
          postId={postId}
          isFlattenedReply
          onChanged={onChanged}
          onLikeToggled={onLikeToggled}
          onAuthRequired={onAuthRequired}
        />
      ))}
    </div>
  );
}

export function CommunityPostDetailPage() {
  const { postId } = useParams();
  const id = Number(postId);
  const nav = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const [post, setPost] = React.useState<CommunityPostDetail | null>(null);
  const [comments, setComments] = React.useState<CommunityComment[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [commentsLoading, setCommentsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [likeLoading, setLikeLoading] = React.useState(false);
  const [bookmarkLoading, setBookmarkLoading] = React.useState(false);
  const [reportLoading, setReportLoading] = React.useState(false);
  const [blockLoading, setBlockLoading] = React.useState(false);
  const [commentText, setCommentText] = React.useState("");
  const [commentImage, setCommentImage] = React.useState<File | null>(null);
  const [commentFile, setCommentFile] = React.useState<File | null>(null);
  const commentImageUrl = useImagePreview(commentImage);
  const [commentSaving, setCommentSaving] = React.useState(false);
  const commentPending = React.useRef(false);
  const [commentError, setCommentError] = React.useState<string | null>(null);
  const requestVersion = React.useRef(0);
  const [vibePreviewStatus, setVibePreviewStatus] = React.useState<
    "idle" | "loaded" | "error"
  >("idle");
  const commentImageInputRef = React.useRef<HTMLInputElement>(null);
  const commentFileInputRef = React.useRef<HTMLInputElement>(null);

  const [authorProfile, setAuthorProfile] =
    React.useState<CommunityUserProfile | null>(null);
  const [authorProfileLoading, setAuthorProfileLoading] = React.useState(false);
  const [nicknameDropdownOpen, setNicknameDropdownOpen] = React.useState(false);
  const [memoFormOpen, setMemoFormOpen] = React.useState(false);
  const [memoInput, setMemoInput] = React.useState("");
  const [memoColor, setMemoColor] = React.useState("#FF0000");
  const [followLoading, setFollowLoading] = React.useState(false);
  const nicknameDropdownRef = React.useRef<HTMLDivElement>(null);

  const canPin = !!(post?.can_pin || user?.is_staff || user?.is_superuser);

  const load = React.useCallback(
    async (refresh = false) => {
      const version = ++requestVersion.current;
      if (!Number.isSafeInteger(id) || id < 1) {
        setError("올바르지 않은 게시글 주소입니다.");
        setLoading(false);
        setCommentsLoading(false);
        return;
      }
      if (!refresh) setLoading(true);
      setCommentsLoading(true);
      setError(null);
      setCommentError(null);
      const [postResult, commentResult] = await Promise.allSettled([
        apiClient.getCommunityPost(id),
        apiClient.listCommunityComments(id),
      ]);
      if (version !== requestVersion.current) return;
      if (postResult.status === "fulfilled") setPost(postResult.value);
      else
        setError(
          postResult.reason instanceof Error
            ? postResult.reason.message
            : "게시글을 불러오지 못했어요.",
        );
      if (commentResult.status === "fulfilled")
        setComments(commentResult.value);
      else setCommentError("댓글을 불러오지 못했어요. 다시 시도해 주세요.");
      setLoading(false);
      setCommentsLoading(false);
    },
    [id],
  );

  const handleCommentLikeToggled = React.useCallback(
    (commentId: number, liked: boolean, numLikes: number) => {
      setComments((prev) =>
        patchCommentLikeInTree(prev, commentId, liked, numLikes),
      );
    },
    [],
  );

  React.useEffect(() => {
    setPost(null);
    setComments([]);
    void load();
    return () => {
      requestVersion.current += 1;
    };
  }, [load]);

  const authorId = post?.author?.id;
  React.useEffect(() => {
    if (!authorId || typeof authorId !== "number") {
      setAuthorProfile(null);
      return;
    }
    let active = true;
    setAuthorProfileLoading(true);
    apiClient
      .getCommunityUserProfile(authorId)
      .then((value) => {
        if (active) setAuthorProfile(value);
      })
      .catch(() => {
        if (active) setAuthorProfile(null);
      })
      .finally(() => {
        if (active) setAuthorProfileLoading(false);
      });
    return () => {
      active = false;
    };
  }, [authorId]);

  React.useEffect(() => {
    if (!nicknameDropdownOpen) return;
    const close = (e: MouseEvent) => {
      if (
        nicknameDropdownRef.current &&
        !nicknameDropdownRef.current.contains(e.target as Node)
      ) {
        setNicknameDropdownOpen(false);
      }
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [nicknameDropdownOpen]);

  const isAuthorMine =
    !!user && !!post?.author?.id && post.author.id === user.id;
  const handleFollowToggle = async () => {
    if (!post?.author?.id || isAuthorMine || followLoading) return;
    setFollowLoading(true);
    try {
      const res = await apiClient.toggleCommunityFollow(post.author.id);
      setAuthorProfile((prev) =>
        prev
          ? {
              ...prev,
              is_following: res.followed,
              followers_count: prev.followers_count + (res.followed ? 1 : -1),
            }
          : prev,
      );
    } catch (error: unknown) {
      setError(
        error instanceof Error
          ? error.message
          : "팔로우 상태를 변경하지 못했어요.",
      );
    } finally {
      setFollowLoading(false);
      setNicknameDropdownOpen(false);
    }
  };
  const handleMemoSave = async () => {
    if (!post?.author?.id || isAuthorMine) return;
    try {
      const res = await apiClient.setCommunityUserMemo(post.author.id, {
        content: memoInput.slice(0, 10),
        color: memoColor,
      });
      setAuthorProfile((prev) =>
        prev
          ? { ...prev, memo: { content: res.content, color: res.color } }
          : prev,
      );
      setMemoFormOpen(false);
      setMemoInput("");
      setNicknameDropdownOpen(false);
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : "메모를 저장하지 못했습니다.");
    }
  };
  const openMemoForm = () => {
    setMemoInput(authorProfile?.memo?.content ?? "");
    setMemoColor(authorProfile?.memo?.color ?? "#FF0000");
    setMemoFormOpen(true);
    setNicknameDropdownOpen(false);
  };

  const postDescription = post
    ? post.content
      ? post.content.replace(/<[^>]*>/g, "").substring(0, 160) + "..."
      : "Foreducator 소통공간 게시글"
    : "Foreducator 소통공간";

  const postUrl =
    id && !Number.isNaN(id) ? `/community/board/${id}` : "/community/board";
  const postImageUrl = post?.head_image_url
    ? getMediaUrl(post.head_image_url)
    : undefined;
  const vibeEmbedUrl = React.useMemo(
    () => resolveEmbeddableHttpsUrl(post?.vibe_coding_link),
    [post?.vibe_coding_link],
  );

  React.useEffect(() => {
    setVibePreviewStatus("idle");
  }, [vibeEmbedUrl]);

  usePageSEO({
    title: post
      ? `${post.title} - Foreducator 소통공간`
      : "Foreducator 소통공간",
    description: postDescription,
    path: postUrl,
    keywords: post
      ? `${post.title}, 교사 커뮤니티, Foreducator 소통공간`
      : "교사 커뮤니티, Foreducator",
    ogImage: postImageUrl,
    pageType: post ? "Article" : "WebPage",
    datePublished: post?.created_at
      ? new Date(post.created_at).toISOString()
      : undefined,
    dateModified: post?.updated_at
      ? new Date(post.updated_at).toISOString()
      : undefined,
    authorName: post?.author?.nickname || post?.author?.username || undefined,
    breadcrumbs: post
      ? [
          { name: "홈", url: "/" },
          { name: "소통공간", url: "/community/board" },
          { name: post.title, url: postUrl },
        ]
      : [
          { name: "홈", url: "/" },
          { name: "소통공간", url: "/community/board" },
        ],
  });

  const toggleLike = async () => {
    if (!post || likeLoading) return;
    if (!isAuthenticated) {
      setError("좋아요는 로그인 후 이용할 수 있어요.");
      return;
    }
    setLikeLoading(true);
    try {
      const res = await apiClient.toggleCommunityPostLike(post.id);
      setPost((prev) =>
        prev
          ? { ...prev, is_liked: res.liked, num_likes: res.num_likes }
          : prev,
      );
    } catch (error: unknown) {
      setError(
        error instanceof Error ? error.message : "좋아요를 저장하지 못했어요.",
      );
    } finally {
      setLikeLoading(false);
    }
  };

  const toggleBookmark = async () => {
    if (!post || bookmarkLoading) return;
    if (!isAuthenticated) {
      setError("북마크는 로그인 후 이용할 수 있어요.");
      return;
    }
    setBookmarkLoading(true);
    try {
      const res = await apiClient.toggleCommunityPostBookmark(post.id);
      setPost((prev) =>
        prev ? { ...prev, is_bookmarked: res.bookmarked } : prev,
      );
    } catch (error: unknown) {
      setError(
        error instanceof Error ? error.message : "북마크를 저장하지 못했어요.",
      );
    } finally {
      setBookmarkLoading(false);
    }
  };

  const reportPost = async () => {
    if (!post) return;
    if (!isAuthenticated) {
      setError("신고는 로그인 후 이용할 수 있어요.");
      return;
    }
    setReportLoading(true);
    try {
      const reason = prompt("신고 사유를 입력해주세요(선택)");
      if (reason === null) return;
      await apiClient.reportCommunityPost(post.id, reason || "");
      alert("신고가 접수되었습니다. 검토 후 조치됩니다.");
    } catch (error: unknown) {
      setError(
        error instanceof Error ? error.message : "신고를 접수하지 못했어요.",
      );
    } finally {
      setReportLoading(false);
    }
  };

  const blockAuthor = async () => {
    if (!post?.author?.id) return;
    if (!isAuthenticated) {
      setError("차단은 로그인 후 이용할 수 있어요.");
      return;
    }
    if (!confirm("이 사용자를 차단할까요? (나에게만 안 보이게 됩니다)")) return;
    setBlockLoading(true);
    try {
      await apiClient.toggleCommunityBlock(post.author.id);
      alert("차단 처리되었습니다.");
      nav("/community/board");
    } catch (error: unknown) {
      setError(
        error instanceof Error ? error.message : "차단을 처리하지 못했어요.",
      );
    } finally {
      setBlockLoading(false);
    }
  };

  const submitComment = async () => {
    if (!post || commentPending.current) return;
    if (!isAuthenticated) {
      setCommentError("댓글은 로그인 후 작성할 수 있어요.");
      return;
    }
    const text = commentText.trim();
    if (!text) return;
    commentPending.current = true;
    setCommentSaving(true);
    setCommentError(null);
    try {
      await apiClient.createCommunityComment(post.id, {
        content: text,
        comment_image: commentImage,
        comment_file: commentFile,
      });
      setCommentText("");
      setCommentImage(null);
      setCommentFile(null);
      await load(true);
    } catch (error: unknown) {
      setCommentError(
        error instanceof Error
          ? error.message
          : "댓글을 저장하지 못했어요. 작성한 내용은 그대로 남아 있어요.",
      );
    } finally {
      commentPending.current = false;
      setCommentSaving(false);
    }
  };

  const isMine =
    !!post &&
    (post.can_edit ||
      (!!user && post.author?.id === user.id) ||
      !!user?.is_superuser);
  const canActions = !!post && isAuthenticated && !isMine && !!post.author?.id;

  const deletePost = async () => {
    if (!post) return;
    if (!confirm("게시글을 삭제할까요?")) return;
    await apiClient.deleteCommunityPost(post.id);
    nav("/community/board");
  };

  const deleteFile = async (fileId: number) => {
    if (!confirm("이 파일을 삭제할까요?")) return;
    await apiClient.deleteCommunityPostFile(fileId);
    await load(true);
  };

  const renderPostHeaderActions = () => {
    if (!post) return null;
    return (
      <>
        {isMine && (
          <>
            <Link to={`/community/board/${post.id}/edit`}>
              <Button variant="secondary">
                <Pencil className="w-4 h-4" />
                수정
              </Button>
            </Link>
            <Button variant="danger" onClick={deletePost}>
              <Trash2 className="w-4 h-4" />
              삭제
            </Button>
          </>
        )}
        {canActions && (
          <>
            <Button
              variant="secondary"
              onClick={reportPost}
              disabled={reportLoading}
            >
              <Flag className="w-4 h-4" />
              신고
            </Button>
            <Button
              variant="secondary"
              onClick={blockAuthor}
              disabled={blockLoading}
            >
              <ShieldOff className="w-4 h-4" />
              차단
            </Button>
          </>
        )}
      </>
    );
  };

  return (
    <div className="fe-page-canvas fe-community">
      <div className="fe-community-layout fe-community-reading">
        <nav className="fe-community-reading-nav" aria-label="소통공간 탐색">
          <Link to="/community/board" className="fe-community-back">
            <ArrowLeft size={16} />
            이야기 목록
          </Link>
          <span className="fe-community-badge">소통공간</span>
        </nav>
        {error && (
          <div role="alert" className="mb-4 text-sm text-red-600">
            {error}
          </div>
        )}

        {loading ? (
          <div className="fe-surface bg-white rounded-3xl border border-gray-200 shadow-sm p-4 sm:p-6 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/3 mb-3" />
            <div className="h-4 bg-gray-200 rounded w-full mb-2" />
            <div className="h-32 bg-gray-200 rounded w-full" />
          </div>
        ) : !post ? (
          <div className="fe-community-empty">
            <h1>게시글을 찾을 수 없어요</h1>
            <p>주소를 확인하거나 이야기 목록에서 다시 찾아보세요.</p>
            <Link to="/community/board" className="fe-community-button">
              목록으로
            </Link>
          </div>
        ) : (
          <>
            <div className="fe-community-article">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-3">
                <div className="min-w-0 flex-1">
                  <h1 className="fe-community-article-title">{post.title}</h1>
                  {(isMine || canActions) && (
                    <div className="mt-3 flex flex-wrap gap-2 md:hidden">
                      {renderPostHeaderActions()}
                    </div>
                  )}
                  <div className="text-sm text-gray-600 mt-2 flex flex-wrap gap-2">
                    <span className="inline-flex flex-wrap items-center gap-2">{getUserDisplayName(post.author)}<SocialLinks links={post.author.social_links} authorName={getUserDisplayName(post.author)} /></span>
                    <span>·</span>
                    <span>
                      {new Date(post.created_at).toLocaleString("ko-KR")}
                    </span>
                    <span>·</span>
                    <span>조회 {post.hits}</span>
                  </div>
                  {post.tags && (
                    <div className="mt-3 text-xs text-gray-500">
                      태그:{" "}
                      {post.tags
                        .split(",")
                        .map((t) => t.trim())
                        .filter(Boolean)
                        .map((t) => (
                          <Link
                            key={t}
                            to={`/community/board?tag=${encodeURIComponent(t)}`}
                            className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 border border-gray-200 text-gray-700 mr-2 hover:bg-gray-200"
                          >
                            {t}
                          </Link>
                        ))}
                    </div>
                  )}

                  {(post.file_upload_url && post.file_upload_name) ||
                  post.additional_files?.length ? (
                    <div className="mt-4 border-t border-gray-100 pt-4">
                      <div className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Paperclip className="w-4 h-4" /> 첨부파일
                      </div>
                      {post.file_upload_url && post.file_upload_name && (
                        <div className="mb-3">
                          <button
                            type="button"
                            onClick={() => {
                              void downloadCommunityMediaFile(
                                post.file_upload_url,
                                post.file_upload_name || "첨부파일",
                              ).catch((err: unknown) => {
                                alert(
                                  err instanceof Error
                                    ? err.message
                                    : "다운로드에 실패했습니다.",
                                );
                              });
                            }}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 bg-gray-50 text-sm text-[#526a2e] hover:bg-gray-100"
                          >
                            <Paperclip className="w-4 h-4" />
                            <span className="truncate max-w-[220px]">
                              {post.file_upload_name}
                            </span>
                          </button>
                        </div>
                      )}
                      {post.additional_files?.length ? (
                        <div className="space-y-2">
                          {post.additional_files.map((f) => (
                            <div
                              key={f.id}
                              className="flex items-center justify-between gap-2"
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  void downloadCommunityMediaFile(
                                    f.url,
                                    f.filename || "첨부파일",
                                  ).catch((err: unknown) => {
                                    alert(
                                      err instanceof Error
                                        ? err.message
                                        : "다운로드에 실패했습니다.",
                                    );
                                  });
                                }}
                                className="text-[#526a2e] hover:underline text-sm truncate text-left"
                              >
                                {f.filename}
                              </button>
                              {isMine && (
                                <button
                                  onClick={() => deleteFile(f.id)}
                                  className="px-2 py-1 rounded-lg text-xs font-semibold border border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                                >
                                  삭제
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                {(isMine || canActions) && (
                  <div className="hidden md:flex flex-wrap gap-2 shrink-0">
                    {renderPostHeaderActions()}
                  </div>
                )}
              </div>

              {post.head_image_url && (
                <div className="mt-6 rounded-2xl overflow-hidden border border-gray-200 bg-gray-50">
                  <img
                    src={getMediaUrl(post.head_image_url)}
                    alt="게시글 대표 이미지"
                    className="w-full h-auto"
                    loading="eager"
                    decoding="async"
                    style={{ maxWidth: "100%", height: "auto" }}
                  />
                </div>
              )}

              {post.vibe_coding_link && (
                <div className="mt-6 rounded-2xl border fe-community-note p-5">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#435536] text-white">
                      <svg
                        className="h-6 w-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-[#35482a]">
                        바이브 코딩 결과
                      </div>
                      <div className="mt-0.5 text-xs text-[#647252] break-all">
                        {post.vibe_coding_link}
                      </div>
                    </div>
                  </div>

                  {vibeEmbedUrl ? (
                    <div className="fe-surface fe-panel mt-4 overflow-hidden rounded-xl border border-[#d8dfc9] bg-white">
                      <div className="aspect-[16/9] min-h-[360px] w-full bg-gray-50">
                        <iframe
                          key={vibeEmbedUrl}
                          src={vibeEmbedUrl}
                          title="바이브 코딩 결과 미리보기"
                          className="h-full w-full border-0"
                          loading="lazy"
                          referrerPolicy="no-referrer"
                          sandbox="allow-forms allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts"
                          allow="clipboard-read; clipboard-write"
                          onLoad={() => setVibePreviewStatus("loaded")}
                          onError={() => setVibePreviewStatus("error")}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
                      보안을 위해 https 링크만 미리보기를 시도합니다. 현재
                      링크는 새 창에서만 열 수 있어요.
                    </div>
                  )}

                  {vibeEmbedUrl && vibePreviewStatus === "error" && (
                    <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
                      이 사이트는 보안 정책으로 iframe 미리보기가 제한될 수
                      있습니다. 아래 버튼으로 새 창에서 확인해 주세요.
                    </div>
                  )}

                  {vibeEmbedUrl && vibePreviewStatus !== "error" && (
                    <div className="mt-3 text-xs text-[#647252]">
                      일부 사이트는 보안 정책에 따라 미리보기가 제한될 수
                      있습니다. 문제가 있으면 아래 버튼으로 새 창에서 열어
                      주세요.
                    </div>
                  )}

                  <div className="mt-4">
                    <a
                      href={post.vibe_coding_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center rounded-lg bg-white/90 px-3 py-1.5 text-sm font-medium text-[#526a2e] shadow-sm transition hover:bg-white"
                    >
                      새 창에서 보기 →
                    </a>
                  </div>
                </div>
              )}

              <div className="mt-6 text-sm text-gray-900 leading-relaxed">
                <CommunityBody html={sanitizeHtml(post.content)} postId={post.id} />
              </div>

              <CommunityPostShare key={post.id} postId={post.id} title={post.title} />

              <div className="mt-6 flex flex-wrap items-center justify-between gap-x-2 gap-y-2 border-t pt-4">
                <div className="flex flex-wrap gap-2">
                  <button
                    disabled={likeLoading}
                    onClick={toggleLike}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold border ${
                      post.is_liked
                        ? "bg-red-50 border-red-200 text-red-700"
                        : "bg-white border-gray-200 text-gray-800"
                    } hover:bg-gray-50 transition`}
                  >
                    <Heart
                      className={`w-4 h-4 ${post.is_liked ? "fill-red-600 text-red-600" : ""}`}
                    />
                    좋아요 {post.num_likes}
                  </button>
                  <button
                    disabled={bookmarkLoading}
                    onClick={toggleBookmark}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold border ${
                      post.is_bookmarked
                        ? "fe-community-selected"
                        : "bg-white border-gray-200 text-gray-800"
                    } hover:bg-gray-50 transition`}
                  >
                    <Bookmark
                      className={`w-4 h-4 ${post.is_bookmarked ? "fill-current text-[#526a2e]" : ""}`}
                    />
                    북마크
                  </button>
                </div>
                <div className="inline-flex items-center gap-2 text-sm text-gray-600">
                  <MessageSquare className="w-4 h-4" /> 댓글 {post.num_comments}
                </div>
              </div>

              {canPin && (
                <div className="mt-3">
                  <button
                    onClick={async () => {
                      const next = !post.top_fixed;
                      await apiClient.updateCommunityPost(post.id, {
                        top_fixed: next,
                      } as any);
                      await load(true);
                    }}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold border ${
                      post.top_fixed
                        ? "bg-amber-50 border-amber-200 text-amber-800"
                        : "bg-white border-gray-200 text-gray-800"
                    } hover:bg-gray-50 transition`}
                  >
                    <Pin className="w-4 h-4" />
                    공지 {post.top_fixed ? "해제" : "설정"}
                  </button>
                </div>
              )}
            </div>

            {/* 작성자 프로필 카드 (메인 글과 댓글 사이) */}
            {post.author?.id && (
              <div className="fe-community-author-card">
                <div className="text-sm font-bold text-gray-700 mb-4">
                  작성자
                </div>
                {authorProfileLoading ? (
                  <div className="flex items-center gap-4 animate-pulse">
                    <div className="w-14 h-14 rounded-full bg-gray-200" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-32" />
                      <div className="h-3 bg-gray-100 rounded w-full max-w-xs" />
                    </div>
                  </div>
                ) : authorProfile ? (
                  <div className="flex flex-wrap gap-4 sm:gap-6">
                    <div className="shrink-0">
                      <CommunityAvatar
                        src={getMediaUrl(authorProfile.avatar_url)}
                        name={getUserDisplayName(authorProfile)}
                        className="w-16 h-16 border-2 border-gray-100 fe-community-avatar text-xl font-bold"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div
                        className="relative flex flex-wrap items-center gap-2"
                        ref={nicknameDropdownRef}
                      >
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isAuthorMine)
                              setNicknameDropdownOpen((v) => !v);
                          }}
                          className="inline-flex items-center gap-1 text-lg font-bold text-gray-900 hover:text-[#526a2e] transition cursor-pointer"
                        >
                          {getUserDisplayName(authorProfile)}
                          {!isAuthorMine && (
                            <ChevronDown
                              className={`w-4 h-4 transition ${nicknameDropdownOpen ? "rotate-180" : ""}`}
                            />
                          )}
                        </button>
                        <SocialLinks links={post.author.social_links} authorName={getUserDisplayName(authorProfile)} />
                        {authorProfile.memo?.content && (
                          <span
                            className="text-xs font-medium px-2 py-0.5 rounded"
                            style={{
                              backgroundColor: `${authorProfile.memo.color ?? "#999"}20`,
                              color: authorProfile.memo.color ?? "#333",
                            }}
                          >
                            {authorProfile.memo.content}
                          </span>
                        )}
                        {!isAuthorMine && nicknameDropdownOpen && (
                          <div className="absolute left-0 top-full mt-1 py-1 w-48 rounded-xl border border-gray-200 bg-white shadow-lg z-[100]">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                nav(
                                  `/messages/compose?to=${encodeURIComponent(authorProfile.username)}`,
                                );
                                setNicknameDropdownOpen(false);
                              }}
                              className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                            >
                              <Send className="w-4 h-4" /> 쪽지보내기
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openMemoForm();
                              }}
                              className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                            >
                              <Pencil className="w-4 h-4" />{" "}
                              {authorProfile.memo?.content
                                ? "메모 수정"
                                : "메모작성"}
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFollowToggle();
                              }}
                              disabled={followLoading}
                              className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
                            >
                              {authorProfile.is_following ? (
                                <UserMinus className="w-4 h-4" />
                              ) : (
                                <UserPlus className="w-4 h-4" />
                              )}
                              {authorProfile.is_following
                                ? "팔로우 끊기"
                                : "팔로우하기"}
                            </button>
                          </div>
                        )}
                      </div>
                      {authorProfile.bio && (
                        <p className="mt-1 text-sm text-gray-600 line-clamp-3">
                          {authorProfile.bio}
                        </p>
                      )}
                      <div className="mt-2 flex flex-wrap gap-4 text-sm text-gray-500">
                        <span>팔로워 {authorProfile.followers_count}</span>
                        <span>팔로잉 {authorProfile.following_count}</span>
                        <span className="font-semibold text-[#526a2e]">
                          {authorProfile.activity_points}P
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <CommunityAvatar
                      src={getMediaUrl(post.author.avatar_url)}
                      name={getUserDisplayName(post.author)}
                      className="w-12 h-12 text-sm"
                    />
                    <span className="font-semibold text-gray-900">
                      {getUserDisplayName(post.author)}
                    </span>
                    <SocialLinks links={post.author.social_links} authorName={getUserDisplayName(post.author)} />
                  </div>
                )}
                {memoFormOpen && post.author?.id && !isAuthorMine && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex flex-wrap items-end gap-2">
                      <input
                        type="text"
                        maxLength={10}
                        value={memoInput}
                        onChange={(e) => setMemoInput(e.target.value)}
                        placeholder="메모 (10자)"
                        className="fe-input rounded-xl border border-gray-300 px-3 py-2 text-sm w-32"
                      />
                      <div className="flex gap-1">
                        {[
                          "#FF0000",
                          "#0000FF",
                          "#FFFF00",
                          "#008000",
                          "#FFA500",
                        ].map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => setMemoColor(c)}
                            className="w-6 h-6 rounded-full border-2 border-gray-200 hover:border-gray-400"
                            style={{
                              backgroundColor: c,
                              borderColor:
                                memoColor === c ? "#374151" : undefined,
                            }}
                          />
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={handleMemoSave}
                        className="fe-primary-action px-3 py-2 rounded-xl bg-[#435536] text-white text-sm font-semibold hover:bg-[#526a2e]"
                      >
                        저장
                      </button>
                      <button
                        type="button"
                        onClick={() => setMemoFormOpen(false)}
                        className="px-3 py-2 rounded-xl border border-gray-200 text-sm hover:bg-gray-50"
                      >
                        취소
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="fe-community-comments">
              <h2 className="text-lg font-bold text-gray-900">
                대화 나누기{" "}
                <span className="fe-community-count">{post.num_comments}</span>
              </h2>
              <p className="fe-community-response-guide">{purposeFromTags(post.tags) === "question" ? "비슷한 상황에서 해본 방법이나 참고 자료를 나눠주세요. 작은 경험도 도움이 됩니다." : purposeFromTags(post.tags) === "showcase" ? "직접 써본 점, 좋았던 점, 개선되면 좋을 점을 만든 선생님께 전해주세요." : "공감한 부분이나 내 교실에서 활용한 경험을 들려주세요."}</p>
              <div className="mt-4">
                <textarea
                  aria-label="댓글 내용"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  className="fe-input w-full rounded-2xl border border-gray-300 px-4 py-3 text-sm min-h-[110px] focus:outline-none focus:ring-2 focus:ring-[#9eb77a]"
                  placeholder={
                    isAuthenticated
                      ? "선생님의 경험과 의견을 들려주세요…"
                      : "로그인 후 댓글을 작성할 수 있어요."
                  }
                  disabled={!isAuthenticated}
                />
                <div className="mt-2 flex flex-wrap gap-3 items-center">
                  {isAuthenticated ? (
                    <label
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-semibold ${
                        commentImage
                          ? "fe-community-selected cursor-pointer"
                          : "border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100 cursor-pointer"
                      }`}
                    >
                      <ImageIcon className="w-4 h-4" />
                      <span>
                        이미지{commentImage ? ` (${commentImage.name})` : ""}
                      </span>
                      <input
                        ref={commentImageInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const compressed = await compressImage(
                              file,
                              1920,
                              0.85,
                            );
                            setCommentImage(compressed);
                          } else {
                            setCommentImage(null);
                          }
                          // 같은 파일을 다시 선택할 수 있도록 value 초기화
                          if (e.target) {
                            e.target.value = "";
                          }
                        }}
                      />
                    </label>
                  ) : (
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-gray-100 bg-gray-50 text-gray-400 text-xs font-semibold cursor-not-allowed opacity-60">
                      <ImageIcon className="w-4 h-4" />
                      <span>이미지</span>
                    </div>
                  )}
                  {isAuthenticated ? (
                    <label
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-semibold ${
                        commentFile
                          ? "fe-community-selected cursor-pointer"
                          : "border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100 cursor-pointer"
                      }`}
                    >
                      <Paperclip className="w-4 h-4" />
                      <span>
                        파일{commentFile ? ` (${commentFile.name})` : ""}
                      </span>
                      <input
                        ref={commentFileInputRef}
                        type="file"
                        className="hidden"
                        onChange={(e) => {
                          setCommentFile(e.target.files?.[0] || null);
                          // 같은 파일을 다시 선택할 수 있도록 value 초기화
                          if (e.target) {
                            e.target.value = "";
                          }
                        }}
                      />
                    </label>
                  ) : (
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-gray-100 bg-gray-50 text-gray-400 text-xs font-semibold cursor-not-allowed opacity-60">
                      <Paperclip className="w-4 h-4" />
                      <span>파일</span>
                    </div>
                  )}
                </div>
                {commentImage && (
                  <div className="mt-2 relative">
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-2 flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <img
                          src={commentImageUrl}
                          alt="미리보기"
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-medium text-gray-700 truncate">
                            {commentImage.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {(commentImage.size / 1024).toFixed(1)} KB
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setCommentImage(null)}
                        className="ml-2 px-2 py-1 rounded-lg text-xs font-semibold border border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                      >
                        삭제
                      </button>
                    </div>
                  </div>
                )}
                {commentFile && !commentImage && (
                  <div className="mt-2">
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-2 flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <Paperclip className="w-5 h-5 text-gray-500 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-medium text-gray-700 truncate">
                            {commentFile.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {(commentFile.size / 1024).toFixed(1)} KB
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setCommentFile(null)}
                        className="ml-2 px-2 py-1 rounded-lg text-xs font-semibold border border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                      >
                        삭제
                      </button>
                    </div>
                  </div>
                )}
                <div className="mt-2 flex justify-end">
                  <button
                    onClick={submitComment}
                    disabled={
                      !isAuthenticated || commentSaving || !commentText.trim()
                    }
                    className="fe-primary-action px-4 py-2.5 rounded-xl font-semibold bg-[#435536] text-white hover:bg-[#526a2e] disabled:opacity-50"
                  >
                    {commentSaving ? "댓글 저장 중…" : "댓글 남기기"}
                  </button>
                </div>
              </div>

              {commentError && (
                <div role="alert" className="fe-community-error">
                  <p>{commentError}</p>
                  <button
                    className="fe-community-button"
                    onClick={() => void load(true)}
                  >
                    댓글 다시 불러오기
                  </button>
                </div>
              )}
              <div className="mt-4 space-y-3">
                {commentsLoading ? (
                  <div className="space-y-3">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div
                        key={i}
                        className="fe-surface rounded-2xl border border-gray-200 bg-white p-4 animate-pulse"
                      >
                        <div className="h-4 bg-gray-200 rounded w-1/4 mb-2" />
                        <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                        <div className="h-4 bg-gray-200 rounded w-3/4" />
                      </div>
                    ))}
                  </div>
                ) : comments.length ? (
                  comments.map((c) => (
                    <CommentItem
                      key={c.id}
                      c={c}
                      postId={post.id}
                      onChanged={() => load(true)}
                      onLikeToggled={handleCommentLikeToggled}
                      onAuthRequired={setError}
                    />
                  ))
                ) : (
                  <div className="text-sm text-gray-500">
                    첫 댓글을 남겨보세요.
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
